<template>
  <div>
    <Modal
      :title="modalTitle"
      :saving="isSaving"
      :disable-submit="editDisabled"
      @hide-modal="$emit('hide-modal')"
      @submit="handleSaveAccountingClass"
      @delete="handleDeleteAccountingClass"
    >
      <warning-message
        v-if="accountingClass?.archived"
        data-cy="archivedMessage"
        class="warning-banner"
      >
        <span>ARCHIVED ACCOUNTING CLASS</span>
      </warning-message>
      <template #delete>
        <SeraButton
          v-if="accountingClass?.links?.archive && accountingClass?.hasUsages"
          data-cy="archiveButton"
          type="danger"
          text="Archive"
          @click="showArchiveConfirm"
        />
        <SeraButton
          v-if="isEditing && !accountingClass?.hasUsages"
          data-cy="deleteButton"
          type="danger"
          text="Delete"
          @click="handleDeleteAccountingClass"
        />
        <SeraButton
          v-else-if="accountingClass?.links?.unarchive"
          data-cy="unarchiveButton"
          text="Unarchive"
          :saving="isUnarchiving"
          outline
          @click="handleUnarchive"
        />
        <!-- Render nothing -->
        <span v-else style="display: none"></span>
      </template>
      <sera-input
        name="className"
        class="full-width"
        :value="className"
        :error="!!formError"
        :disabled="editDisabled"
        data-cy="accounting-class-name"
        @change="handleTextChange"
      >
        <template #label>
          <div>
            <span class="field-label">Accounting Class Name:</span>
          </div>
        </template>
      </sera-input>
      <div v-if="editDisabled">
        <i class="fas fa-question-circle" />
        This accounting class has been linked to QuickBooks Online, and is now
        managed there. Please make your changes there and click "verify" at the
        top of the accounting classes list.
      </div>
    </Modal>

    <!-- Archive Delete Confirmation -->
    <ArchiveAccountingClassModal
      v-if="archiveConfirmVisible"
      :accounting-class="accountingClass"
      @hide-modal="handleHideArchiveConfirm"
      @archived="handleArchiveComplete"
    />
  </div>
</template>

<script lang="ts">
import { mapActions } from "vuex";

import SeraButton from "@/components/Button.vue";
import { AccountingClass } from "@/typings/accounting";
import Modal from "../components/Modal.vue";
import ArchiveAccountingClassModal from "./ArchiveAccountingClassModal.vue";

export default {
  components: {
    ArchiveAccountingClassModal,
    SeraButton,
    Modal
  },
  emits: ["hide-modal", "save-success"],
  props: {
    accountingClass: {
      type: Object as () => AccountingClass,
      default: null
    }
  },
  data() {
    return {
      className: "",
      loadingArchiveConfirmData: null,
      archiveConfirmResults: null,
      archiveConfirmVisible: false,
      isUnarchiving: false,
      isSaving: false,
      isEditing: false,
      formError: null
    };
  },
  computed: {
    modalTitle() {
      return this.isEditing
        ? "Edit Accounting Class"
        : "Create Accounting Class";
    },
    editDisabled() {
      if (this.accountingClass?.qbo_status === "verified") {
        return true;
      }

      if (this.accountingClass?.qbo_status === "inactive") {
        return true;
      }

      return false;
    }
  },
  mounted() {
    if (this.accountingClass && this.accountingClass.id) {
      this.isEditing = true;
      this.className = this.accountingClass.name;
    }
  },
  methods: {
    ...mapActions([
      "createAccountingClass",
      "updateAccountingClass",
      "deleteAccountingClass",
      "unarchiveAccountingClass",
      "confirmDelete"
    ]),
    async handleArchiveComplete() {
      this.closeModalWithSuccess("archived");
    },
    async showArchiveConfirm() {
      this.archiveConfirmVisible = true;
    },
    handleHideArchiveConfirm() {
      this.archiveConfirmVisible = false;
    },
    async handleUnarchive() {
      this.isUnarchiving = true;
      try {
        await this.unarchiveAccountingClass(this.accountingClass);

        this.closeModalWithSuccess("unarchived");
      } catch (e) {
        console.error(e);
      }
      this.isUnarchiving = false;
    },
    handleSaveAccountingClass() {
      this.isSaving = true;
      const accountingAction = this.isEditing
        ? this.updateAccountingClass
        : this.createAccountingClass;
      const payload = {
        accounting_class: {
          ...this.accountingClass,
          name: this.className
        }
      };
      accountingAction(payload)
        .then(() => {
          const action = this.isEditing ? "edited" : "created";
          this.closeModalWithSuccess(action);
        })
        .catch((ex) => {
          this.formError = ex.message;
          this.isSaving = false;
        });
    },
    closeModalWithSuccess(action: string) {
      window.notify.success(
        `Accounting Class has been ${action} successfully!`
      );
      this.isSaving = false;
      this.$emit("save-success");
      this.$emit("hide-modal");
    },
    handleDeleteAccountingClass() {
      this.confirmDelete().then(() => {
        this.deleteAccountingClass(this.accountingClass.id)
          .then(() => {
            window.notify.success("Accounting Class deleted successfully!");
            this.$emit("save-success");
          })
          .finally(() => {
            this.$emit("hide-modal");
          });
      });
    },
    toggleDeleteModal() {
      this.confirmDeleteModal = !this.confirmDeleteModal;
    },
    handleTextChange({ detail }) {
      this.className = detail;
    }
  }
};
</script>

<style lang="scss" scoped></style>
